/**
 * HTML tables → LaTeX `tabular`: rows and cells read with the HTML table model
 * (colspan, rowspan, header cells), column alignment taken from the source or guessed
 * from numeric columns, and rules written as booktabs, `\hline` or a full grid.
 */

import type { ElementNode, HtmlNode } from "@/lib/tool-kit/developer/html/tree";
import {
  attr,
  type Ctx,
  flatten,
  styleMap,
} from "@/lib/tool-kit/latex-dev/rich-text-inline";

type Align = "l" | "c" | "r";

interface Cell {
  latex: string;
  colspan: number;
  rowspan: number;
  header: boolean;
  align?: Align;
  /** A slot covered by a cell from an earlier row's rowspan. */
  covered: boolean;
}

interface SourceRow {
  cells: Cell[];
  inHead: boolean;
}

const RE_NUMERIC = /^[-+−]?(?:\d[\d,]*(?:\.\d+)?|\.\d+)%?$/;
const RE_SPAN = /^\d{1,4}$/;

/** Cell content is produced by the block converter; tables only place it. */
export type CellContent = (children: HtmlNode[], ctx: Ctx) => string;

function alignOf(el: ElementNode): Align | undefined {
  const value = (
    styleMap(el).get("text-align") ??
    attr(el, "align") ??
    ""
  ).toLowerCase();
  if (value === "center") {
    return "c";
  }
  if (value === "right" || value === "end") {
    return "r";
  }
  if (value === "left" || value === "start") {
    return "l";
  }
  return undefined;
}

function span(el: ElementNode, name: string): number {
  const value = (attr(el, name) ?? "1").trim();
  const parsed = RE_SPAN.test(value) ? Number.parseInt(value, 10) : 1;
  return Math.min(Math.max(parsed, 1), 100);
}

function readRow(
  tr: ElementNode,
  inHead: boolean,
  ctx: Ctx,
  content: CellContent
): SourceRow {
  const cells: Cell[] = [];
  for (const child of tr.children) {
    if (
      child.kind !== "element" ||
      (child.name !== "td" && child.name !== "th")
    ) {
      continue;
    }
    const rowspan = span(child, "rowspan");
    if (rowspan > 1) {
      ctx.stats.rowspans += 1;
    }
    cells.push({
      latex: flatten(content(child.children, ctx)),
      colspan: span(child, "colspan"),
      rowspan,
      header: child.name === "th",
      align: alignOf(child),
      covered: false,
    });
  }
  return { cells, inHead };
}

function gatherRows(
  nodes: HtmlNode[],
  inHead: boolean,
  ctx: Ctx,
  content: CellContent,
  rows: SourceRow[]
): void {
  for (const node of nodes) {
    if (node.kind !== "element") {
      continue;
    }
    if (node.name === "tr") {
      rows.push(readRow(node, inHead, ctx, content));
    } else if (
      node.name === "thead" ||
      node.name === "tbody" ||
      node.name === "tfoot"
    ) {
      gatherRows(node.children, node.name === "thead", ctx, content, rows);
    }
  }
}

function blankCell(): Cell {
  return {
    latex: "",
    colspan: 1,
    rowspan: 1,
    header: false,
    covered: true,
  };
}

/** Place cells on the grid, filling slots a rowspan from above still covers. */
function layout(rows: SourceRow[]): SourceRow[] {
  const covered = new Map<number, number>();
  const placed: SourceRow[] = [];
  for (const row of rows) {
    const cells: Cell[] = [];
    let col = 0;
    const fillCovered = () => {
      while ((covered.get(col) ?? 0) > 0) {
        covered.set(col, (covered.get(col) ?? 1) - 1);
        cells.push(blankCell());
        col += 1;
      }
    };
    for (const cell of row.cells) {
      fillCovered();
      cells.push(cell);
      for (let i = 0; i < cell.colspan; i += 1) {
        if (cell.rowspan > 1) {
          covered.set(col + i, cell.rowspan - 1);
        }
      }
      col += cell.colspan;
    }
    fillCovered();
    while ([...covered.entries()].some(([c, n]) => c >= col && n > 0)) {
      cells.push(blankCell());
      covered.set(col, Math.max((covered.get(col) ?? 1) - 1, 0));
      col += 1;
    }
    placed.push({ cells, inHead: row.inHead });
  }
  return placed;
}

function rowWidth(row: SourceRow): number {
  return row.cells.reduce((sum, cell) => sum + cell.colspan, 0);
}

function headerCount(rows: SourceRow[], forceFirst: boolean): number {
  let count = 0;
  for (const row of rows) {
    const isHead =
      row.inHead ||
      (row.cells.length > 0 &&
        row.cells.every((cell) => cell.header || cell.covered));
    if (!isHead) {
      break;
    }
    count += 1;
  }
  if (count >= rows.length) {
    count = Math.min(1, rows.length);
  }
  return count === 0 && forceFirst && rows.length > 1 ? 1 : count;
}

/** One alignment per column: the source's, else `r` for all-numeric columns, else `l`. */
function columnAligns(rows: SourceRow[], width: number, head: number): Align[] {
  const aligns: Align[] = [];
  for (let col = 0; col < width; col += 1) {
    let explicit: Align | undefined;
    let numeric = true;
    let seen = 0;
    for (const [index, row] of rows.entries()) {
      const cell = cellAt(row, col);
      if (cell === undefined || cell.colspan !== 1) {
        continue;
      }
      explicit ??= cell.align;
      if (index >= head && cell.latex !== "") {
        seen += 1;
        numeric &&= RE_NUMERIC.test(cell.latex);
      }
    }
    aligns.push(explicit ?? (seen > 0 && numeric ? "r" : "l"));
  }
  return aligns;
}

function cellAt(row: SourceRow, col: number): Cell | undefined {
  let at = 0;
  for (const cell of row.cells) {
    if (col >= at && col < at + cell.colspan) {
      return cell;
    }
    at += cell.colspan;
  }
  return undefined;
}

function renderRow(
  row: SourceRow,
  width: number,
  header: boolean,
  ctx: Ctx
): string {
  const grid = ctx.opts.tableRules === "grid";
  const parts: string[] = [];
  let col = 0;
  for (const cell of row.cells) {
    let text = cell.latex;
    if (header && ctx.opts.boldHeader && text !== "") {
      text = `\\textbf{${text}}`;
    }
    if (cell.colspan > 1) {
      const spec = `${grid && col === 0 ? "|" : ""}${cell.align ?? "c"}${grid ? "|" : ""}`;
      text = `\\multicolumn{${cell.colspan}}{${spec}}{${text}}`;
    }
    parts.push(text);
    col += cell.colspan;
  }
  for (; col < width; col += 1) {
    parts.push("");
  }
  return `${parts.join(" & ")} \\\\`;
}

function rulesFor(ctx: Ctx): {
  top: string;
  mid: string;
  bottom: string;
  row: string;
} {
  switch (ctx.opts.tableRules) {
    case "booktabs":
      ctx.packages.add("booktabs");
      return {
        top: "\\toprule",
        mid: "\\midrule",
        bottom: "\\bottomrule",
        row: "",
      };
    case "hline":
      return { top: "\\hline", mid: "\\hline", bottom: "\\hline", row: "" };
    case "grid":
      return { top: "\\hline", mid: "", bottom: "", row: "\\hline" };
    default:
      return { top: "", mid: "", bottom: "", row: "" };
  }
}

function captionOf(table: ElementNode, ctx: Ctx, content: CellContent): string {
  for (const child of table.children) {
    if (child.kind === "element" && child.name === "caption") {
      return flatten(content(child.children, ctx));
    }
  }
  return "";
}

/** The `tabular` for a table element, in a `table` float when the options ask for it. */
export function renderTable(
  table: ElementNode,
  ctx: Ctx,
  content: CellContent
): string {
  const raw: SourceRow[] = [];
  gatherRows(table.children, false, ctx, content, raw);
  const rows = layout(raw.filter((row) => row.cells.length > 0));
  if (rows.length === 0) {
    return "";
  }
  ctx.stats.tables += 1;
  const width = Math.max(...rows.map(rowWidth));
  const head = headerCount(rows, ctx.opts.firstRowHeader);
  const aligns = columnAligns(rows, width, head);
  const grid = ctx.opts.tableRules === "grid";
  const spec = grid ? `|${aligns.join("|")}|` : aligns.join("");
  const rules = rulesFor(ctx);
  const lines = [`\\begin{tabular}{${spec}}`];
  if (rules.top) {
    lines.push(rules.top);
  }
  for (const [index, row] of rows.entries()) {
    lines.push(renderRow(row, width, index < head, ctx));
    if (rules.row) {
      lines.push(rules.row);
    }
    if (index === head - 1 && rules.mid && index < rows.length - 1) {
      lines.push(rules.mid);
    }
  }
  if (rules.bottom) {
    lines.push(rules.bottom);
  }
  lines.push("\\end{tabular}");
  const caption = captionOf(table, ctx, content);
  if (!ctx.opts.tableFloat || ctx.inCell) {
    return lines.join("\n");
  }
  const float = ["\\begin{table}[htbp]", "\\centering"];
  if (caption !== "") {
    float.push(`\\caption{${caption}}`);
  }
  float.push(...lines, "\\end{table}");
  return float.join("\n");
}
